import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, del, get, patch, post, put, withQuery } from "./client";
import type { SiteRef } from "./quotations";

/** `/api/v1/boq`, as `prv.web.api.boq` shapes it. */

export type BoqStatus =
  | "pending_generation"
  | "draft"
  | "generated"
  | "submitted"
  | "approved"
  | "returned"
  | "rejected"
  | "superseded";

export interface ContractRef {
  id: number;
  name: string;
  code?: string;
}

export interface BoqRow {
  id: number;
  name: string;
  status: BoqStatus;
  contract: ContractRef | null;
  site: SiteRef | null;
  project_no: string | null;
  boq_date: string | null;
  line_count: number;
  total_excl_vat: string;
  unpriced: number;
  approved_at: string | null;
  approved_by: string | null;
  invoice: { id: number; number: string } | null;
  quotation_id: number | null;
  /** A pre-issued bill the visit has not confirmed yet. */
  provisional: boolean;
  /** The office's draft BOQ, priced before the supplier quoted; never the City's bill. */
  is_draft: boolean;
  work_order_no: string | null;
  /** The job the bill is worked on: its quotation, or for a draft the job that
   * quotes its site and work order -- null while no supplier has quoted it. */
  job_id: number | null;
  /** A draft the customer BOQ has been copied from, and so finished with. */
  copied: boolean;
}

export interface BoqLine {
  item_id: number;
  rate_item_id: number;
  item_no: string;
  payment_ref: string;
  description: string;
  unit: string | null;
  rate: string | null;
  /** Said in the rate column instead of the card's rate: K2's "5% of K1". */
  rate_note: string | null;
  schedule: string;
  qty: string;
  line_total: string | null;
}

export interface RateMatch {
  id: number;
  schedule: string;
  item_no: string;
  payment_ref: string;
  description: string;
  unit: string | null;
  rate: string | null;
  bore_mm: number | null;
  on_boq: boolean;
}

/** What a pre-issued bill was priced on before anybody visited
 * (`prv.boq.pre_issue`). Null on every other bill. */
export interface BoqEstimate {
  basis: "kit" | "worst_case";
  kit: {
    size_label: string;
    dn_mm: number;
    series: string | null;
    series_rule: "stated" | "dearer" | "only" | null;
    supplier: { id: number; name: string } | null;
    price_list_version: number | null;
  } | null;
  k1_estimate: string | null;
  attendance_rate: string;
  journey: { km: string | null; approximate: boolean; meeting_point: { id: number; name: string } | null };
  notes: string[];
  model_confirmed: { at: string | null; by: string | null; model: string | null } | null;
  built_at: string | null;
  built_by: string | null;
}

/** A pre-issued bill against the approved quotation that reprices it. */
export interface BoqReprice {
  boq_id: number;
  quotation: { id: number; number: string };
  k1_now: string | null;
  k1_quoted: string;
  k2_quoted: string;
  k1_estimate: string | null;
  higher: boolean;
  applied: boolean;
  locked_reason: string | null;
}

/** A figure on the bill a person replaced, with the reason they gave. */
export interface BoqOverride {
  payment_ref: string | null;
  field: string;
  calculated: string | null;
  revised: string;
  reason: string | null;
  by: string | null;
  role: string | null;
  at: string | null;
}

/** Where B15's hours came from on a pre-issued bill: the labour allowance for
 * its make and DN (or the default), raised to the minimum charge it was built
 * with. Null on a bill with no estimate. */
export interface BoqB15 {
  hours_on_bill: string | null;
  allowance_hours: string;
  matched: "make" | "any_make" | "default";
  allowance: { id: number; label: string; supervisor_hours: string; active: boolean } | null;
  minimum_charge: string | null;
  extra_hours: string;
  /** B15 still holds what the system wrote; false once the office changed it. */
  system_owned: boolean;
  sentence: string | null;
}

/** The supplier labour the bill already carries inside K1. */
export interface SupplierLabour {
  source: "quotation" | "kit";
  quotation: { id: number; number: string } | null;
  kit_size_label: string | null;
  lines: string[];
}

/** A rate-card labour line beside the supplier's labour: labour billed twice,
 * until it is removed or kept with a reason. */
export interface DoubleLabourLine {
  item_id: number;
  payment_ref: string;
  description: string;
  kept: boolean;
  kept_reason: string | null;
  kept_by: string | null;
}

export interface BoqLabour {
  b15: BoqB15 | null;
  supplier_labour: SupplierLabour | null;
  double_labour: DoubleLabourLine[];
}

export interface BoqDetail {
  id: number;
  name: string;
  status: BoqStatus;
  project_no: string | null;
  project_desc: string | null;
  scope: string | null;
  site_label: string | null;
  boq_date: string | null;
  created_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  /** When the bill was issued to the City, and by whom (an email address). */
  issued_at: string | null;
  issued_by: string | null;
  contract: ContractRef | null;
  site: SiteRef | null;
  region_name: string | null;
  quotation: { id: number; number: string } | null;
  inspection: { id: number; work_order_no: string } | null;
  lines: BoqLine[];
  total_excl_vat: string;
  unpriced: string[];
  schedules: string[];
  route_plans: { id: number; name: string; km: string | null }[];
  km_rates: RateMatch[];
  revisions: {
    revision_no: number;
    at: string | null;
    by: string | null;
    reason: string;
    status: string;
    total_excl_vat: string;
  }[];
  /** The draft BOQ: step 1, priced by hand before the supplier quoted, never
   * issued to the City. Found by the job through its works order. */
  is_draft: boolean;
  work_order_no: string | null;
  /** On a draft: the customer BOQ copied from it, which freezes it. */
  copied_to: { id: number; name: string } | null;
  /** On a customer BOQ: the draft it was copied from. */
  copied_from: { id: number; name: string } | null;
  /** On a customer BOQ copied from a draft: the approved quotation's full
   * price, offered for K1, and whether K1 already says it. */
  quote_k1: { amount: string; matches: boolean } | null;
  invoice: { id: number; number: string } | null;
  export_blocked: string | null;
  /** The workbook downloaded now is a draft copy: the bill is not approved. */
  draft_copy: boolean;
  /** Whether a remittance exists: only a bill generated from a QCP has the
   * worst-case draft it compares against. */
  has_remittance: boolean;
  /** Why the bill cannot be invoiced now; null when it can. */
  invoice_blocked: string | null;
  /** Pre-issued and not yet confirmed by the visit (BRS C-19). */
  provisional: boolean;
  provisional_reason: string | null;
  estimate: BoqEstimate | null;
  reprice: BoqReprice | null;
  overrides: BoqOverride[];
  labour: BoqLabour;
  /** Why the bill cannot be issued now (its status, a pending reprice, labour
   * billed twice); null when it can. */
  issue_blocked: string | null;
  actions: {
    can_edit: boolean;
    can_issue: boolean;
    can_withdraw: boolean;
    can_unapprove: boolean;
    can_delete: boolean;
    can_invoice: boolean;
    can_reprice: boolean;
    /** K1 is typed as an amount: a draft BOQ, or a customer BOQ copied from one. */
    can_set_k1: boolean;
    /** The approved quotation's price is offered for K1 and differs from it. */
    can_take_quote_k1: boolean;
    /** Remove and Keep are offered: an editable bill with a flagged line not yet kept. */
    can_resolve_labour: boolean;
  };
  urls: { xlsm: string; json: string; pdf: string; remittance: string; customer_summary: string };
}

export const BOQS_KEY = ["boq"] as const;
export const boqKey = (id: number) => ["boq", id] as const;

/** Every bill, for a picker or the documents ledger. `enabled` is false where
 * the bill is already known and the list would be fetched for nothing. */
export function useBoqs(search: string, enabled = true) {
  return useQuery<{ rows: BoqRow[]; contracts: ContractRef[] }, ApiError>({
    queryKey: [...BOQS_KEY, { search }],
    queryFn: () => get<{ rows: BoqRow[]; contracts: ContractRef[] }>(withQuery("/api/v1/boq", { search })),
    enabled,
  });
}

export function useBoq(id: number | null) {
  return useQuery<BoqDetail, ApiError>({
    queryKey: boqKey(id ?? 0),
    queryFn: () => get<BoqDetail>(`/api/v1/boq/${id}`),
    enabled: id !== null,
  });
}

export function useRateSearch(boqId: number, query: string, schedule: string) {
  return useQuery<{ matches: RateMatch[]; truncated: boolean }, ApiError>({
    queryKey: ["boq", boqId, "rates", { query, schedule }],
    queryFn: () =>
      get<{ matches: RateMatch[]; truncated: boolean }>(
        withQuery(`/api/v1/boq/${boqId}/rates`, { query, schedule }),
      ),
    enabled: query.trim().length > 0 || schedule !== "",
    staleTime: 60_000,
  });
}

export function useSiteSearch(q: string) {
  return useQuery<{ rows: SiteRef[]; truncated: boolean }, ApiError>({
    queryKey: ["sites", "search", q],
    queryFn: () => get<{ rows: SiteRef[]; truncated: boolean }>(withQuery("/api/v1/sites/search", { q })),
    enabled: q.trim().length > 1,
    staleTime: 60_000,
  });
}

function useBoqWrite<TVars>(write: (vars: TVars) => Promise<BoqDetail>) {
  const client = useQueryClient();
  return useMutation<BoqDetail, ApiError, TVars>({
    mutationFn: write,
    onSuccess: (detail) => {
      client.setQueryData(boqKey(detail.id), detail);
      void client.invalidateQueries({ queryKey: BOQS_KEY });
      void client.invalidateQueries({ queryKey: ["quotations"] });
      // The site drawer shows the pre-issued bill's total.
      void client.invalidateQueries({ queryKey: ["sites"] });
    },
  });
}

export interface CreateBoq {
  contract_id: number;
  name: string;
  site_id: number | null;
  project_no: string;
  project_desc: string;
  scope: string;
  boq_date: string | null;
  seed: boolean;
}

export function useCreateBoq() {
  return useBoqWrite((body: CreateBoq) => post<BoqDetail>("/api/v1/boq", body));
}

/** Start the draft BOQ for a site's job under its works order: blank, priced
 * by hand. The server refuses a second live draft for the same job. */
export function useStartDraftBoq() {
  return useBoqWrite((body: { site_id: number; work_order_no: string }) => post<BoqDetail>("/api/v1/boq/drafts", body));
}

/** K1 typed as an amount on a hand-priced bill; K2 follows. "0" takes both off. */
export function useSetK1(id: number) {
  return useBoqWrite((amount: string) => put<BoqDetail>(`/api/v1/boq/${id}/k1`, { amount }));
}

/** K1 on a customer BOQ copied from a draft takes the approved quotation's full price. */
export function useTakeQuoteK1(id: number) {
  return useBoqWrite(() => post<BoqDetail>(`/api/v1/boq/${id}/quote-k1`));
}

export function useUpdateBoqHeader(id: number) {
  return useBoqWrite(
    (body: {
      name: string;
      project_no: string | null;
      project_desc: string | null;
      scope: string | null;
      site_label: string | null;
      boq_date: string | null;
    }) => patch<BoqDetail>(`/api/v1/boq/${id}`, body),
  );
}

export function useSetBoqLine(id: number) {
  return useBoqWrite((body: { rate_item_id: number; qty: string }) => put<BoqDetail>(`/api/v1/boq/${id}/items`, body));
}

export function useRemoveBoqLine(id: number) {
  return useBoqWrite((itemId: number) => del<BoqDetail>(`/api/v1/boq/${id}/items/${itemId}`));
}

export function useAddDistance(id: number) {
  return useBoqWrite((body: { rate_item_id: number; plan_id: number }) =>
    post<BoqDetail>(`/api/v1/boq/${id}/distance`, body),
  );
}

/** Issue the bill to the City or take it back to a draft, or unapprove it. The
 * City's checked PO approves a bill, so there is no office approve. The server refuses a move the bill's state does not
 * allow, with its reason as the error message. */
export type BoqMove = "issue" | "withdraw" | "unapprove";

export function useBoqMove(id: number) {
  return useBoqWrite((move: BoqMove) => post<BoqDetail>(`/api/v1/boq/${id}/${move}`));
}

/** Reprice a linked pre-issued bill from its approved quotation. A quote above
 * the kit price needs the reason; the server refuses without one. */
export function useRepriceBoq(id: number) {
  return useBoqWrite((reason: string | null) => post<BoqDetail>(`/api/v1/boq/${id}/reprice`, { reason }));
}

/** Keep a rate-card labour line beside the supplier's labour, with the reason
 * the override register keeps. */
export function useKeepLabourLine(id: number) {
  return useBoqWrite((vars: { itemId: number; reason: string }) =>
    post<BoqDetail>(`/api/v1/boq/${id}/labour/${vars.itemId}/keep`, { reason: vars.reason }),
  );
}

/** Take a rate-card labour line off the bill: the supplier's K1 carries it. */
export function useRemoveLabourLine(id: number) {
  return useBoqWrite((itemId: number) => post<BoqDetail>(`/api/v1/boq/${id}/labour/${itemId}/remove`));
}

export function useDeleteBoq() {
  const client = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (id) => del<void>(`/api/v1/boq/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: BOQS_KEY });
      // The job's Customer BOQ step goes back to "not raised".
      void client.invalidateQueries({ queryKey: ["quotations"] });
    },
  });
}

export function useIssueInvoice(id: number) {
  const client = useQueryClient();
  return useMutation<{ invoice_id: number; url: string; existing: boolean }, ApiError>({
    mutationFn: () => post<{ invoice_id: number; url: string; existing: boolean }>(`/api/v1/boq/${id}/invoice`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: boqKey(id) });
      void client.invalidateQueries({ queryKey: ["invoices"] });
      // The job moves to done, and its Invoice step fills in.
      void client.invalidateQueries({ queryKey: ["quotations"] });
    },
  });
}

export const BOQ_STATUS_LABEL: Record<BoqStatus, string> = {
  pending_generation: "Pending",
  draft: "Draft",
  generated: "Generated",
  // The stored status is still "submitted"; the office calls it Issued.
  submitted: "Issued",
  approved: "Approved",
  returned: "Returned",
  rejected: "Rejected",
  superseded: "Superseded",
};
